<template>
  <div v-if="isOpen" class="sp_v2_content_panel">
    <div v-if="content" class="sp_v2_content_panel_header">
      <div class="sp_v2_content_panel_title">
        <span class="sp_v2_content_name">{{ content.name }}</span>
      </div>
      <div class="sp_v2_content_panel_actions">
        <button class="sp_v2_nav_btn" :disabled="!hasPrev" @click="$emit('prev')" title="Previous">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>
        <button class="sp_v2_nav_btn" :disabled="!hasNext" @click="$emit('next')" title="Next">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>
      </div>
    </div>

    <div v-else class="sp_v2_content_panel_header sp_v2_empty_header">
      <span class="sp_v2_empty_header_label">Select content</span>
    </div>

    <div class="sp_v2_content_panel_body" v-if="content">
      <div class="sp_v2_content_viewer_container">
        <iframe v-show="!['pdf', 'image', 'video', 'youtube'].includes(content.content_extension)" class="sp_v2_content_iframe" :src="getIframeSrc()" allow="autoplay" />

        <img v-if="content.content_extension === 'image'" :src="content.url" class="sp_v2_content_image" />

        <PdfViewer v-if="content.content_extension === 'pdf'" :key="content.url" :url="content.url" @onScroll="handleProgress" />

        <VideoPlayer v-if="content.content_extension === 'video'" :src="content.url" :poster="content.image" @onProgress="handleProgress" />

        <YoutubeVideoPlayer v-if="content.content_extension === 'youtube'" :src="content.url" />
      </div>

      <div v-if="!content.url && content.content_type !== 'tour'" class="sp_v2_content_fallback">
        <div class="sp_v2_fallback_icon">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M13 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V9L13 2Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M13 2V9H20" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </div>
        <p class="sp_v2_fallback_text">Content preview not available</p>
        <a v-if="content.content_url || content.url" :href="content.content_url || content.url" target="_blank" class="sp_v2_fallback_link">Open in new tab</a>
      </div>
    </div>

    <div v-else class="sp_v2_content_panel_body sp_v2_content_empty">
      <div class="sp_v2_content_fallback">
        <div class="sp_v2_fallback_icon">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="7" height="7" x="3" y="3" rx="1" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            <rect width="7" height="7" x="14" y="3" rx="1" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            <rect width="7" height="7" x="3" y="14" rx="1" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            <rect width="7" height="7" x="14" y="14" rx="1" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </div>
        <p class="sp_v2_fallback_text">Select content from playlist</p>
      </div>
    </div>
  </div>
</template>

<script>
import PdfViewer from "./components/PdfViewer/index.vue";
import VideoPlayer from "./components/VideoPlayer/index.vue";
import YoutubeVideoPlayer from "./components/YoutubeVideoPlayer/index.vue";
import MediaQueryMixin from "../mixins/mediaQuery.js";
import { getTourShareCode } from "@/utils";

export default {
  name: "ContentPanelV2",
  mixins: [MediaQueryMixin],
  components: {
    PdfViewer,
    VideoPlayer,
    YoutubeVideoPlayer,
  },
  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
    content: {
      type: Object,
      default: null,
    },
    sessionId: {
      type: String,
      default: null,
    },
    playlist: {
      type: Array,
      default: () => [],
    },
    hasPrev: {
      type: Boolean,
      default: false,
    },
    hasNext: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    getContentTypeLabel(extension) {
      const labels = {
        pdf: "PDF Document",
        video: "Video",
        youtube: "YouTube Video",
        html: "Web Content",
        article: "Article",
        image: "Image",
        tour: "Tour",
        "web page": "Web Page",
      };
      return labels[extension] || "Content";
    },
    getContentUrl(content) {
      if (content.content_extension === "youtube") {
        return null;
      } else if (content.content_extension === "web page") {
        return content.url;
      }
      return content.url;
    },
    getIframeSrc() {
      if (!this.content) return "";

      if (this.content.content_type === "tour") {
        const query = this.sessionId ? { interaction: this.sessionId } : {};
        return getTourShareCode({
          baseUrl: process.env.VUE_APP_SHARE_URL,
          publicId: this.content.content_public_id,
          query,
        });
      }

      return this.getContentUrl(this.content);
    },
    handleProgress(data) {
      this.$emit("progress", {
        content: this.content,
        ...data,
      });
    },
  },
};
</script>

<style lang="scss">
.sp_v2_content_panel {
  flex: 1;
  min-width: 0;
  height: 100%;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-right: 1px solid #e5e7ea;
}

.sp_v2_content_panel_header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  border-bottom: 1px solid #e5e7ea;
  background: #f9f9f9;
  flex-shrink: 0;
}

.sp_v2_content_panel_title {
  flex: 1;
  min-width: 0;
}

.sp_v2_content_name {
  font-size: 14px;
  font-weight: 600;
  color: #060e2b;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
}

.sp_v2_content_panel_actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.sp_v2_content_action_btn {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: none;
  background: transparent;
  color: #9ca3af;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  padding: 0;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #ffffff;
  }

  svg {
    width: 16px;
    height: 16px;
  }
}

.sp_v2_empty_header {
  gap: 12px;
  justify-content: flex-start;

  .sp_v2_empty_header_label {
    font-size: 12px;
    font-weight: 500;
    color: #9ca3af;
  }
}

.sp_v2_content_empty {
  display: flex;
  align-items: center;
  justify-content: center;
}

.sp_v2_nav_btn {
  width: 32px;
  height: 32px;
  min-width: 32px;
  border-radius: 50%;
  border: 1px solid #e5e7ea;
  background: #ffffff;
  color: #6b7280;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #f2f3f5;
    color: #060e2b;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

.sp_v2_content_panel_body {
  flex: 1;
  overflow: hidden;
  position: relative;
}

.sp_v2_content_viewer_container {
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
}

.sp_v2_content_iframe {
  border: 0;
  width: 100%;
  height: 100%;
  display: block;
}

.sp_v2_content_image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}

.sp_v2_content_fallback {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 40px;
  text-align: center;
}

.sp_v2_fallback_icon {
  color: #6b7280;
  margin-bottom: 16px;

  svg {
    width: 48px;
    height: 48px;
  }
}

.sp_v2_fallback_text {
  font-size: 16px;
  color: #9ca3af;
  margin: 0 0 16px 0;
}

.sp_v2_fallback_link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: var(--sp-chat-primary);
  color: #ffffff;
  text-decoration: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-2px);
  }
}

@media (max-width: 1023px) {
  .sp_v2_content_panel {
    max-height: 80%;
  }
}
</style>
